import axios from 'axios'
import Database from '@/assets/javascript/database'
import uuid from 'uuid/v4'

// Initial state
const state = {
  palletNumberSequence: null,
  palletNumberSequenceUpdated: null,
  pallets: [],
  suppliers: [],
  fieldStatuses: [],
  processing: false
}

// Mutations
const mutations = {
  setPalletNumberSequence (state, sequence) {
    state.palletNumberSequence = sequence
  },
  setPalletNumberSequenceUpdated (state, updated) {
    state.palletNumberSequenceUpdated = updated
  },
  setPallets (state, pallets) {
    state.pallets = pallets
  },
  updatePallet (state, pallet) {
    for (let i in state.pallets) {
      if (state.pallets[i].number === pallet.number) {
        state.pallets[i] = pallet
        break
      }
    }
  },
  updateSuppliers (state, suppliers) {
    state.suppliers = suppliers
  },
  updateFieldStatuses (state, fieldStatuses) {
    state.fieldStatuses = fieldStatuses
  },
  setProcessing (state, processing) {
    state.processing = processing
  }
}

// Actions
const actions = {
  loadPalletNumberSequence ({ commit, state }, ctx) {
    let userName = ctx.userName
    let sequence = localStorage.getItem('PalletNumberSequence_' + userName)
    let updated = localStorage.getItem('PalletNumberSequenceUpdated_' + userName)
    if (sequence) {
      commit('setPalletNumberSequence', sequence)
    }
    if (updated) {
      commit('setPalletNumberSequenceUpdated', updated)
    }

    // Read from the server
    axios.get('/backend/rest/pallet_number/sequence')
      .then(response => {
        let palletNumberSequence = response.data
        if (!state.palletNumberSequence || state.palletNumberSequenceUpdated !== palletNumberSequence.updated) {
          commit('setPalletNumberSequence', palletNumberSequence.sequence)
          commit('setPalletNumberSequenceUpdated', palletNumberSequence.updated)

          let userName = palletNumberSequence.userName
          localStorage.setItem('PalletNumberSequence_' + userName, palletNumberSequence.sequence)
          localStorage.setItem('PalletNumberSequenceUpdated_' + userName, palletNumberSequence.updated)
        }
      })
      .catch(error => {
        console.log('Failed to get pallet number sequence: ' + error)
      })
  },
  incrementPalletNumberSequence ({ commit, state }, ctx) {
    let userName = ctx.userName
    let sequence = parseInt(state.palletNumberSequence) + 1
    commit('setPalletNumberSequence', sequence)
    localStorage.setItem('PalletNumberSequence_' + userName, sequence)
  },
  decrementPalletNumberSequence ({ commit, state }, ctx) {
    let userName = ctx.userName
    let sequence = parseInt(state.palletNumberSequence) - 1
    commit('setPalletNumberSequence', sequence)
    localStorage.setItem('PalletNumberSequence_' + userName, sequence)
  },
  setPalletNumberSequence ({ commit, state }, ctx) {
    let userName = ctx.userName
    let sequence = parseInt(ctx.sequence)
    commit('setPalletNumberSequence', sequence)
    localStorage.setItem('PalletNumberSequence_' + userName, sequence)
  },
  loadPallets ({ commit, state, dispatch }, ctx) {
    return new Promise((resolve, reject) => {
      // console.log('Get all pallets for contractor')

      let pallets = []
      // Call the backend
      axios.get('/backend/rest/pallet/contractor')
        .then(response => {
          let pallets = response.data
          // console.log('Got ' + pallets.length + ' pallets from server')
          commit('setPallets', pallets)
          // Store the pallets in local db
          let db = new Database()
          if (pallets) {
            db.doInTransaction('ContractorPallets', function (tx) {
              let contractorPallets = tx.objectStore('ContractorPallets')

              contractorPallets.clear()

              if (pallets.length > 0) {
                for (let pallet of pallets) {
                  contractorPallets.put(pallet, pallet.id)
                }
              }
            })
          }
        })
        .catch(error => {
          console.log('Failed to get pallets: ' + error)
          reject(Error(error))

          // Read the pallets from local db
          // console.log('Read pallets in the database')
          let db = new Database()
          db.doInTransaction('ContractorPallets', function (tx) {
            let objectStore = tx.objectStore('ContractorPallets')

            objectStore.openCursor().onsuccess = function (event) {
              var cursor = event.target.result
              if (cursor) {
                pallets.push(cursor.value)
                cursor.continue()
              } else {
                // console.log('Found ' + pallets.length + ' pallets in database')
                commit('setPallets', pallets)
                resolve('Success')
              }
            }
          })
        })
    })
  },
  startPallet ({ commit, state, rootState, getters, dispatch }, pallet) {
    return new Promise((resolve, reject) => {
      // console.log('Start production on pallet ' + pallet.number)
      if (!pallet.uuid || pallet.uuid === '') {
        pallet.uuid = uuid()
      }
      pallet.state = 'IN_PRODUCTION'
      if (!pallet.items || pallet.items.length === 0) {
        pallet.items = [
          {
            id: 0,
            uuid: uuid(),
            categoryName: '',
            plannedAmount: 0,
            amount: 0,
            manual: true
          }
        ]
      }
      commit('updatePallet', pallet)
      dispatch('savePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'START_PALLET',
          pallet: pallet,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
    })
  },
  /* Deprecated */
  updatePalletNumber ({ commit, state, dispatch }, pallet) {
    return new Promise((resolve, reject) => {
      // console.log('Start production on pallet ' + pallet.number)
      commit('updatePallet', pallet)
      dispatch('savePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'UPDATE_PALLET_NUMBER',
          pallet: pallet,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
    })
  },
  cancelPallet ({ commit, state, rootState, getters, dispatch }, pallet) {
    return new Promise((resolve, reject) => {
      // console.log('End production on pallet ' + pallet.number)
      if (pallet.number !== '') {
        let supplierNumber = pallet.supplier.number
        let sequence = getters['getPalletNumberSequence']
        let cancellablePalletNumber = supplierNumber + sequence - 1
        if (Number(pallet.number) === cancellablePalletNumber) {
          pallet.number = null
          dispatch('decrementPalletNumberSequence', { userName: rootState.session.userName })
        }
      }
      pallet.state = 'PLANNED'
      commit('updatePallet', pallet)
      dispatch('deletePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'CANCEL_PALLET',
          pallet: pallet,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
    })
  },
  endPallet ({ commit, state, rootState, getters, dispatch }, pallet) {
    return new Promise((resolve, reject) => {
      // console.log('End production on pallet ' + pallet.number)
      if (!pallet.number || pallet.number === '') {
        let supplierNumber = pallet.supplier.number
        let sequence = getters['getPalletNumberSequence']
        pallet.number = supplierNumber + sequence
        dispatch('incrementPalletNumberSequence', { userName: rootState.session.userName })
      }
      pallet.state = 'PRODUCED'
      commit('updatePallet', pallet)
      dispatch('savePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'END_PALLET',
          pallet: pallet,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
      resolve('Succeeded')
    }).finally(() => {
      dispatch('checkSendContractorCommands', { root: true })
    })
  },
  savePalletItem ({ commit, state, dispatch }, ctx) {
    return new Promise((resolve, reject) => {
      let pallet = ctx.pallet
      let item = ctx.item
      if (!item.uuid || item.uui === '') {
        item.uuid = uuid()
      }
      // console.log('Save amount ' + item.amount + ' on pallet item ' + item.id + ' on pallet ' + pallet.number)
      commit('updatePallet', pallet)
      dispatch('savePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'UPDATE_PALLET_ITEM',
          pallet: pallet,
          itemId: item.id,
          itemUuid: item.uuid,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
    })
  },
  deletePalletItem ({ commit, state, dispatch }, ctx) {
    return new Promise((resolve, reject) => {
      let pallet = ctx.pallet
      let item = ctx.item
      // console.log('Delete pallet item ' + item.id + ' on pallet ' + pallet.number)
      commit('updatePallet', pallet)
      dispatch('savePalletInProduction', pallet)

      let db = new Database()
      db.doInTransaction('ContractorCommands', function (tx) {
        let contractorCommands = tx.objectStore('ContractorCommands')
        let command = {
          command: 'DELETE_PALLET_ITEM',
          pallet: pallet,
          itemId: item.id,
          itemUuid: item.uuid,
          timestamp: new Date()
        }
        contractorCommands.put(command)
      })
    })
  },
  updatePallet ({ commit, state }, ctx) {
    return new Promise((resolve, reject) => {
      let pallet = ctx.pallet
      // console.log('Update pallet ' + JSON.stringify(pallet))
      commit('updatePallet', pallet)
    })
  },
  savePalletInProduction ({ commit, state }, pallet) {
    return new Promise((resolve, reject) => {
      let palletsInProduction = []
      let jsonPallets = localStorage.getItem('PalletsInProduction')
      if (jsonPallets) {
        palletsInProduction = JSON.parse(jsonPallets)
      }

      let found = false
      for (let i in palletsInProduction) {
        if (palletsInProduction[i].uuid === pallet.uuid) {
          palletsInProduction[i] = pallet
          found = true
          break
        }
      }
      if (!found) {
        palletsInProduction.push(pallet)
      }
      localStorage.setItem('PalletsInProduction', JSON.stringify(palletsInProduction))
    })
  },
  loadSuppliers ({ commit }) {
    return new Promise((resolve, reject) => {
      axios.get('/backend/rest/supplier/production')
        .then(response => {
          let suppliers = response.data
          commit('updateSuppliers', suppliers)
        })
    })
  },
  loadFieldStatuses ({ commit }) {
    return new Promise((resolve, reject) => {
      axios.get('/backend/rest/field/status/contractor')
        .then(response => {
          let fieldStatuses = response.data
          commit('updateFieldStatuses', fieldStatuses)
        })
    })
  },
  deletePalletInProduction ({ commit, state }, pallet) {
    return new Promise((resolve, reject) => {
      let jsonPallets = localStorage.getItem('PalletsInProduction')
      if (jsonPallets) {
        let palletsInProduction = JSON.parse(jsonPallets).filter(function (palletInProduction) {
          return palletInProduction.uuid !== pallet.uuid
        })
        localStorage.setItem('PalletsInProduction', JSON.stringify(palletsInProduction))
      }
    })
  },
  checkSendContractorCommands ({ commit, state, rootGetters, dispatch }) {
    if (!state.processing) {
      setTimeout(() => {
        dispatch('fetchSendContractorCommands')
      }, 25)
    }
  },
  fetchSendContractorCommands ({ commit, state, rootGetters, dispatch }) {
    // console.log('Fetch and send contractor commands')
    if (rootGetters['session/hasToken'] && !rootGetters['session/hasAdministratorRole'] && !state.processing) {
      commit('setProcessing', true)
      let db = new Database()
      // console.log('Starting database transaction')
      db.doInTransaction('ContractorCommands', function (tx) {
        // console.log('About to open object store')
        let objectStore = tx.objectStore('ContractorCommands')
        // console.log('Got object store')
        let commandRecords = []
        objectStore.openCursor().onsuccess = function (event) {
          // console.log('Got database cursor')
          var cursor = event.target.result
          if (cursor) {
            let key = cursor.key
            let contractorCommand = cursor.value
            commandRecords.push({ key: key, command: contractorCommand })
            cursor.continue()
          } else {
            // console.log('Found ' + commandRecords.length + ' command records')
            let status = {}
            status.number = commandRecords.length
            status.timestamp = commandRecords.length > 0 ? commandRecords[0].command.timestamp : null
            dispatch('status/updateContractorCommandStatus', status, { root: true })
            let online = navigator.onLine
            if (online) {
              let ctx = {
                database: db,
                commandRecords: commandRecords
              }
              dispatch('sendContractorCommands', ctx)
            } else {
              // console.log('Not online')
              commit('setProcessing', false)
            }
          }
        }
      })
    } else if (state.processing) {
      // console.log('Skipping cycle as it is currently processing')
    }
  },
  sendContractorCommands ({ commit, state, dispatch }, ctx) {
    let db = ctx.database
    let commandRecords = ctx.commandRecords
    if (commandRecords && commandRecords.length > 0) {
      // console.log('Sending ' + commandRecords.length + ' contractor command' + (commandRecords.length > 1 ? 's' : ''))
      let commandRecord = commandRecords.shift()
      let key = commandRecord.key
      let contractorCommand = commandRecord.command
      delete contractorCommand.pallet.supplier.firstPickupDate
      delete contractorCommand.pallet.field.firstPickupDate
      // console.log('Process command ' + key)
      // console.log('Sending contractor command ' + key)
      axios.post('/backend/rest/command/contractor', contractorCommand)
        .then((response) => {
          // console.log('Contractor command sent successfully')
          db.doInTransaction('ContractorCommands', function (tx) {
            tx.objectStore('ContractorCommands').delete(key)
          })
          if (contractorCommand.command === 'END_PALLET') {
            dispatch('deletePalletInProduction', contractorCommand.pallet)
          }
          if (commandRecords.length > 0) {
            dispatch('sendContractorCommands', ctx)
          } else {
            // console.log('Done sending contractor commands')
            commit('setProcessing', false)
            dispatch('loadPallets')
            dispatch('loadSuppliers')
            dispatch('loadFieldStatuses')
          }
        })
        .catch((e) => {
          console.log('Failed to send contractor command: ', e)
          commit('setProcessing', false)
        })
    } else {
      commit('setProcessing', false)
    }
  }
}

// Getters
const getters = {
  getPalletNumberSequence: (state) => {
    let palletNumberSequence = state.palletNumberSequence
    if (typeof palletNumberSequence === 'number') {
      palletNumberSequence = palletNumberSequence.toString()
    }
    if (palletNumberSequence !== '') {
      while (palletNumberSequence.length < 4) {
        palletNumberSequence = '0' + palletNumberSequence
      }
    }
    return palletNumberSequence
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/contractor.js